/**
 * Carrier shipments at the end of the line: the weighing side, and the state
 * of the shipment towards the carrier (creation, label, cancellation).
 *
 * The PS130 weighs when the barcode is beeped, which can happen after the
 * parcel is closed. These functions put that weight on the closed parcel, and
 * guard a label that already exists: once the carrier has one, a different
 * weight cannot be written silently, it has to surface as a problem.
 */
import { COMPANY_TIMEZONE } from '../common/day.js';
import { accept, refuse } from '../common/outcome.js';
import { awaitsFinishedBoxes } from './manual-order.js';
import { cratesOf } from './warehouse.js';
export const REWEIGH_TOLERANCE_KG = 0.5;
export const REWEIGH_TOLERANCE_RATIO = 0.1;
/** BRT takes the weight with format 5.1: three integer digits, one decimal. */
const MAX_MANUAL_KG = 999.9;
function weighedDifferently(old, fresh) {
    const tolerance = Math.max(REWEIGH_TOLERANCE_KG, old * REWEIGH_TOLERANCE_RATIO);
    return Math.abs(fresh - old) > tolerance;
}
/**
 * Writes weight and volume on closed parcels that have none. `pesate` is keyed
 * by parcel barcode. A parcel that already has a weight is never overwritten;
 * if its shipment already has a carrier label and the new weight is clearly
 * different, the shipment gets a `Collo ripesato` problem instead.
 */
export function applyWeighings(s, pesate) {
    const spedizioni = {};
    let changed = false;
    for (const [ordine, sp] of Object.entries(s.spedizioni)) {
        const created = sp.vettore?.fase === 'creata';
        // Closed without a label of ours (pick-up, the old gesture, before BRT):
        // the goods are gone, a weighing has nothing left to say about them.
        if (sp.chiusa && !created) {
            spedizioni[ordine] = sp;
            continue;
        }
        let reweighed = null;
        const colli = sp.colli.map((c) => {
            const p = pesate[c.barcode];
            if (!p || !c.chiuso)
                return c;
            if (c.pesoKg !== null) {
                if (created && p.pesoKg !== null && weighedDifferently(c.pesoKg, p.pesoKg))
                    reweighed = c;
                return c;
            }
            if (created || p.pesoKg === null)
                return c;
            return { ...c, pesoKg: p.pesoKg, volumeCm3: p.volumeCm3 };
        });
        const parcelsChanged = colli.some((c, i) => c !== sp.colli[i]);
        if (!parcelsChanged && reweighed === null) {
            spedizioni[ordine] = sp;
            continue;
        }
        changed = true;
        const flagged = reweighed;
        spedizioni[ordine] =
            flagged && sp.vettore
                ? {
                    ...sp,
                    colli,
                    vettore: {
                        ...sp.vettore,
                        problema: {
                            titolo: 'Collo ripesato',
                            motivo: `Il collo ${flagged.barcode} è stato pesato di nuovo con un valore diverso da ` +
                                `${flagged.pesoKg} kg, ma l'etichetta è già stata creata: controlla il peso.`,
                            campi: [],
                        },
                    },
                }
                : { ...sp, colli };
    }
    return changed ? { ...s, spedizioni: { ...s.spedizioni, ...spedizioni } } : s;
}
/** Rule 37: a closed parcel without a weight can get one typed by hand. */
export function manualWeight(s, ordine, progressivo, pesoKg, chi, quando) {
    const sp = s.spedizioni[ordine];
    const collo = sp?.colli.find((c) => c.progressivo === progressivo);
    if (!sp || !collo) {
        return refuse('37', 'Collo sconosciuto', `Su ${ordine} non c'è il collo ${progressivo}.`);
    }
    if (!collo.chiuso) {
        return refuse('37', 'Collo ancora aperto', 'Il collo si pesa quando si chiude: chiudilo prima.');
    }
    if (collo.pesoKg !== null) {
        return refuse('37', 'Collo già pesato', `${collo.barcode} ha già un peso: ${collo.pesoKg} kg.`);
    }
    if (!Number.isFinite(pesoKg) || pesoKg <= 0 || pesoKg > MAX_MANUAL_KG) {
        return refuse('37', 'Peso non valido', 'Scrivi un peso in kg maggiore di zero e fino a 999,9.');
    }
    const colli = sp.colli.map((c) => c === collo ? { ...c, pesoKg, pesoManuale: { chi, quando: quando.toISOString() } } : c);
    return accept({ ...s, spedizioni: { ...s.spedizioni, [ordine]: { ...sp, colli } } }, 'Peso scritto a mano', `${collo.barcode}: ${pesoKg} kg.`);
}
// ---------------------------------------------------------------------------
// Shipment state towards the carrier. All of it is pure: the HTTP calls and
// the clock live outside, which hand their outcome to these functions.
// ---------------------------------------------------------------------------
export const CARRIER_UNREACHABLE_LIMIT_MS = 30 * 60_000;
export const CANCEL_RETRY_LIMIT_MS = 10 * 60_000;
const RULE = '31';
const CANCEL_NOT_CONFIRMED = 'Annullamento non riuscito';
const EMPTY_CONSIGNEE = {
    nome: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: null,
    nazione: '',
    telefono: null,
    email: null,
};
/**
 * The problem title of a shipment without a consignee: the carrier is not
 * really chosen yet, the rule picks it again from the address typed in.
 */
export const MISSING_ADDRESS = "Manca l'indirizzo";
/** Where carriers differ on the consignee: the one source for the checks and the bench. */
export function consigneeLimits(corriere) {
    return corriere === 'GLS'
        ? { indirizzoMax: 40, soloItalia: true }
        : { indirizzoMax: 35, soloItalia: false };
}
/**
 * The carrier's limits on the consignee: the fields that would be rejected.
 * BRT takes addresses up to 35 characters and any country; GLS takes up to 40
 * but only Italy.
 */
export function consigneeProblems(c, corriere) {
    const bad = [];
    const limits = consigneeLimits(corriere);
    const nome = c.nome.trim();
    const indirizzo = c.indirizzo.trim();
    const cap = c.cap.trim();
    const citta = c.citta.trim();
    if (nome.length < 1 || nome.length > 70)
        bad.push('nome');
    if (indirizzo.length < 6 || indirizzo.length > limits.indirizzoMax)
        bad.push('indirizzo');
    if (cap.length < 1 || cap.length > 9)
        bad.push('cap');
    if (citta.length < 1 || citta.length > 35)
        bad.push('citta');
    if (c.provincia !== null && c.provincia !== '' && !/^[A-Za-z]{2}$/.test(c.provincia)) {
        bad.push('provincia');
    }
    const nazione = limits.soloItalia ? c.nazione.trim().toUpperCase() : c.nazione;
    if (!/^[A-Za-z]{2}$/.test(nazione))
        bad.push('nazione');
    else if (limits.soloItalia && nazione !== 'IT')
        bad.push('nazione');
    return bad;
}
function parcelsReady(sp) {
    return sp.colli.length > 0 && sp.colli.every((c) => c.chiuso && c.pesoKg !== null);
}
/** Crates of the order still outside the boxes: goods that would stay behind. */
function cratesOutside(s, ordine) {
    return cratesOf(s, ordine)
        .filter((c) => !c.imballata)
        .map((c) => c.codice);
}
/**
 * Orders in the plan whose parcels are all closed, weighed and packed, with no
 * carrier yet. A hand-opened order has no crates to say when it is all in:
 * it waits for «Scatole finite» too. A shipment already closed is out: it left with the old manual
 * gesture, or before BRT was wired, and sending it again would ship twice.
 */
export function readyForCarrier(s) {
    return s.piano
        .filter((o) => {
        if (o.ritiro)
            return false;
        const sp = s.spedizioni[o.ordine];
        if (!sp || sp.vettore || sp.chiusa)
            return false;
        if (awaitsFinishedBoxes(o))
            return false;
        if (!parcelsReady(sp))
            return false;
        return cratesOutside(s, o.ordine).length === 0;
    })
        .map((o) => o.ordine);
}
/** Local `HH:MM` in the company's time zone, for the texts the bench reads. */
const ORA = new Intl.DateTimeFormat('it-IT', {
    timeZone: COMPANY_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
function requestFor(sp, ordine, riferimento, destinatario, corriere) {
    const kg = sp.colli.reduce((tot, c) => tot + (c.pesoKg ?? 0), 0);
    const volumes = sp.colli.map((c) => c.volumeCm3);
    const volumeM3 = volumes.some((v) => v === null)
        ? null
        : Math.round(volumes.reduce((tot, v) => tot + (v ?? 0), 0) / 1000) / 1000;
    return {
        ordine,
        riferimento,
        destinatario,
        colli: sp.colli.length,
        pesoKg: Math.round(kg * 10) / 10,
        volumeM3,
        corriere,
        pesiColli: [...sp.colli]
            .sort((a, b) => a.progressivo - b.progressivo)
            .map((c) => Math.round((c.pesoKg ?? 0) * 10) / 10),
    };
}
function withCarrier(s, ordine, sp, vettore) {
    return { ...s, spedizioni: { ...s.spedizioni, [ordine]: { ...sp, vettore } } };
}
/**
 * Another order holding live labels in the same carrier shipment (GLS merges
 * orders to the same consignee), or `undefined`. BRT labels have no shipment.
 */
function otherHolder(s, ordine, spedizione) {
    if (!spedizione)
        return undefined;
    return Object.values(s.spedizioni).find((x) => x.ordine !== ordine &&
        x.vettore?.etichette.some((l) => !l.annullata && l.spedizione === spedizione))?.ordine;
}
/**
 * A shipment that still has live labels cannot be redone nor re-addressed:
 * the carrier holds those parcels. On a merged one (to fix) the way out is
 * the person's decision; on a created one, the cancellation.
 */
function liveLabelsRefusal(s, ordine, v, spedizione) {
    const altro = otherHolder(s, ordine, spedizione);
    const cosa = v.fase === 'da-sistemare'
        ? 'Decidi in WebLabeling, poi premi “Restano insieme” o chiedi all’ufficio.'
        : 'Se va rifatta, annullala prima.';
    return refuse(RULE, 'Etichette ancora valide', `${ordine} ha etichette ${v.corriere} non annullate` +
        (altro ? `, nella stessa spedizione di ${altro}` : '') +
        `: non si rifà da qui. ${cosa}`);
}
/** Reads the shipment and its carrier state, refusing when the phase is not an allowed one. */
function inPhase(s, ordine, fasi, azione) {
    const sp = s.spedizioni[ordine];
    const v = sp?.vettore;
    if (!sp || !v) {
        return { rifiuto: refuse(RULE, 'Nessuna spedizione col corriere', `${ordine}: ${azione}.`) };
    }
    if (!fasi.includes(v.fase)) {
        return {
            rifiuto: refuse(RULE, 'Spedizione in un altro stato', `${ordine} è «${v.fase}»: ${azione}.`),
        };
    }
    return { sp, v };
}
/**
 * Starts the creation of the shipment at the carrier. Accepted from «no
 * carrier» and from `da-sistemare`, and from `creazione` only when the outcome
 * of the previous attempt is unknown and that reference has been confirmed
 * gone. A missing or invalid consignee is accepted too, but goes straight to
 * `da-sistemare` with the reason and no request.
 *
 * An order the plan has let go is still accepted when its shipment already
 * went to the carrier once: the plan drops an order as soon as its labels
 * exist, and a later cancellation must be retryable. The consignee then comes
 * from the shipment itself, and the crate check still holds: crates belong to
 * the order, not to the plan.
 */
export function startCarrier(s, ordine, riferimento, adesso, corriere) {
    void adesso;
    const sp = s.spedizioni[ordine];
    const o = s.piano.find((p) => p.ordine === ordine);
    if (!sp || (!o && !sp.vettore)) {
        return refuse(RULE, 'Ordine sconosciuto', `${ordine} non è in piano.`);
    }
    if (o?.ritiro)
        return refuse(RULE, 'Ritiro in sede', `${ordine} lo ritira il cliente.`);
    if (sp.chiusa && !sp.vettore) {
        return refuse(RULE, 'Spedizione già chiusa', `${ordine} è già stata chiusa senza ${sp.corriere ?? 'il corriere'}: la merce è partita.`);
    }
    if (awaitsFinishedBoxes(o)) {
        return refuse(RULE, 'Scatole non finite', `${ordine}: premi “Scatole finite” quando hai messo tutta la merce nelle scatole.`);
    }
    const fuori = cratesOutside(s, ordine);
    if (fuori.length > 0) {
        return refuse(RULE, 'Merce fuori dalle scatole', `${ordine}: ${fuori.join(', ')} non sono state svuotate.`);
    }
    if (!parcelsReady(sp)) {
        return refuse(RULE, 'Colli non pronti', `${ordine}: tutti i colli devono essere chiusi e pesati.`);
    }
    const old = sp.vettore ?? null;
    const live = old?.etichette.find((e) => !e.annullata);
    if (old && live)
        return liveLabelsRefusal(s, ordine, old, live.spedizione);
    if (old && old.fase !== 'da-sistemare' && !(old.fase === 'creazione' && old.esitoIncerto)) {
        return refuse(RULE, 'Spedizione già avviata', `${ordine} è già «${old.fase}» presso il corriere.`);
    }
    const destinatario = old?.destinatario ?? o?.destinatario ?? null;
    const previous = old ? [...old.riferimentiPrecedenti, old.riferimento] : [];
    const base = {
        corriere,
        fase: 'creazione',
        riferimento,
        riferimentiPrecedenti: previous,
        esitoIncerto: false,
        // A retry after a timeout keeps the clock: the limit must not reset forever.
        irraggiungibileDa: old?.fase === 'creazione' ? old.irraggiungibileDa : null,
        annullamentoDa: null,
        destinatario: destinatario ?? EMPTY_CONSIGNEE,
        correzione: old?.correzione ?? null,
        normalizzato: null,
        // Cancelled labels stay on record, marked.
        etichette: (old?.etichette ?? []).filter((e) => e.annullata),
        problema: null,
    };
    const campi = destinatario ? consigneeProblems(destinatario, corriere) : [];
    if (!destinatario || campi.length > 0) {
        const problema = destinatario
            ? {
                titolo: `Indirizzo non valido per ${corriere}`,
                motivo: `Controlla i campi: ${campi.join(', ')}.`,
                campi,
            }
            : {
                titolo: MISSING_ADDRESS,
                motivo: "L'ordine non ha un indirizzo di consegna: scrivilo a mano.",
                campi: ['nome', 'indirizzo', 'cap', 'citta', 'nazione'],
            };
        return accept(withCarrier(s, ordine, sp, { ...base, fase: 'da-sistemare', problema }), problema.titolo, `${ordine}: ${problema.motivo}`, null);
    }
    return accept(withCarrier(s, ordine, sp, base), 'Spedizione avviata', `${ordine}: richiesta al corriere.`, requestFor(sp, ordine, riferimento, destinatario, corriere));
}
/** The carrier made the labels: they pair with the parcels in order of `progressivo`. */
export function carrierCreated(s, ordine, colli, normalizzato, unitaAltri = null) {
    const r = inPhase(s, ordine, ['creazione'], 'non sta aspettando etichette');
    if ('rifiuto' in r)
        return r.rifiuto;
    const parcels = [...r.sp.colli].sort((a, b) => a.progressivo - b.progressivo);
    if (colli.length !== parcels.length) {
        return refuse(RULE, 'Etichette e colli non tornano', `${ordine} ha ${parcels.length} colli e il corriere ha restituito ${colli.length} etichette.`);
    }
    const etichette = parcels.map((p, i) => ({ ...colli[i], collo: p.barcode, annullata: false }));
    const joined = etichette
        .map((e) => ({ altro: otherHolder(s, ordine, e.spedizione), spedizione: e.spedizione }))
        .find((x) => x.altro !== undefined);
    // Another order of ours explains the extra parcels better than strangers do.
    const problema = joined
        ? {
            titolo: 'Unita a un altro ordine',
            motivo: `${r.v.corriere} ha messo ${ordine} nella stessa spedizione di ${joined.altro} (${joined.spedizione}): annullarla annullerebbe anche l'altro ordine. Decidi tu.`,
            campi: [],
        }
        : unitaAltri
            ? {
                titolo: "Unita a un'altra spedizione",
                motivo: `${r.v.corriere} ha messo ${ordine} in una spedizione con ${unitaAltri.totaleColli} colli: non sono tutti nostri. Annullarla annullerebbe anche gli altri. Decidi tu.`,
                campi: [],
            }
            : null;
    const next = {
        ...r.sp,
        chiusa: true,
        corriere: r.v.corriere,
        vettore: {
            ...r.v,
            fase: problema ? 'da-sistemare' : 'creata',
            esitoIncerto: false,
            irraggiungibileDa: null,
            normalizzato,
            etichette: [...r.v.etichette, ...etichette],
            problema,
        },
    };
    if (problema) {
        return accept({ ...s, spedizioni: { ...s.spedizioni, [ordine]: next } }, problema.titolo, `${ordine}: ${problema.motivo}`);
    }
    return accept({ ...s, spedizioni: { ...s.spedizioni, [ordine]: next } }, 'Etichette create', `${ordine}: ${etichette.length} etichette pronte.`);
}
/** The carrier refused the request: someone has to fix it. */
export function carrierRefused(s, ordine, problema) {
    const r = inPhase(s, ordine, ['creazione'], 'non sta aspettando una risposta');
    if ('rifiuto' in r)
        return r.rifiuto;
    return accept(withCarrier(s, ordine, r.sp, { ...r.v, fase: 'da-sistemare', problema }), problema.titolo, `${ordine}: ${problema.motivo}`);
}
/** The carrier did not answer: the outcome is unknown until the limit, then someone looks. */
export function carrierUnreachable(s, ordine, motivo, adesso, limiteMs) {
    const r = inPhase(s, ordine, ['creazione'], 'non sta aspettando una risposta');
    if ('rifiuto' in r)
        return r.rifiuto;
    const since = r.v.irraggiungibileDa ?? adesso.toISOString();
    const expired = adesso.getTime() - new Date(since).getTime() > limiteMs;
    const next = expired
        ? {
            ...r.v,
            esitoIncerto: true,
            irraggiungibileDa: since,
            fase: 'da-sistemare',
            problema: {
                titolo: `${r.v.corriere} non risponde`,
                motivo: `Nessuna risposta da ${r.v.corriere} dalle ${ORA.format(new Date(since))}: ${motivo}`,
                campi: [],
            },
        }
        : { ...r.v, esitoIncerto: true, irraggiungibileDa: since };
    return accept(withCarrier(s, ordine, r.sp, next), expired ? `${r.v.corriere} non risponde` : `${r.v.corriere} non raggiungibile`, `${ordine}: ${motivo}`);
}
/**
 * Someone corrected the consignee of a shipment to fix. The first correction
 * keeps the original. `corriere` is the carrier the rule picks for the new
 * consignee: the one whose limits apply, written on the shipment. Not while
 * the outcome of a request is unknown: that request is checked with the
 * carrier written on it, and the next booking asks the rule again anyway.
 */
export function correctConsignee(s, ordine, nuovo, chi, adesso, corriere) {
    const r = inPhase(s, ordine, ['da-sistemare'], 'si corregge solo ciò che è da sistemare');
    if ('rifiuto' in r)
        return r.rifiuto;
    const live = r.v.etichette.find((e) => !e.annullata);
    if (live)
        return liveLabelsRefusal(s, ordine, r.v, live.spedizione);
    const campi = consigneeProblems(nuovo, corriere);
    if (campi.length > 0) {
        return refuse(RULE, `Indirizzo non valido per ${corriere}`, `Controlla i campi: ${campi.join(', ')}.`);
    }
    return accept(withCarrier(s, ordine, r.sp, {
        ...r.v,
        corriere: r.v.esitoIncerto ? r.v.corriere : corriere,
        destinatario: nuovo,
        correzione: r.v.correzione ?? {
            chi,
            quando: adesso.toISOString(),
            originale: r.v.destinatario,
        },
    }), 'Indirizzo corretto', `${ordine}: indirizzo corretto da ${chi}.`);
}
/**
 * «Ho deciso: restano insieme». A shipment the carrier merged with another
 * (ours or not) keeps its labels and travels: it becomes «created». Only from
 * that state: to fix, with live labels.
 */
export function keepMerged(s, ordine, adesso) {
    void adesso;
    const r = inPhase(s, ordine, ['da-sistemare'], 'restano insieme solo spedizioni unite');
    if ('rifiuto' in r)
        return r.rifiuto;
    if (!r.v.etichette.some((e) => !e.annullata)) {
        return refuse(RULE, 'Nessuna spedizione unita', `${ordine} non ha etichette valide: non c'è niente da tenere insieme.`);
    }
    return accept(withCarrier(s, ordine, r.sp, { ...r.v, fase: 'creata', problema: null }), 'Restano insieme', `${ordine}: la spedizione resta com'è, etichette valide.`);
}
/** Asks the carrier to cancel the shipment: only one that exists. */
export function startCancel(s, ordine, adesso, chi) {
    const r = inPhase(s, ordine, ['creata'], "si annulla solo una spedizione con l'etichetta creata");
    if ('rifiuto' in r)
        return r.rifiuto;
    const shared = r.v.etichette
        .filter((e) => !e.annullata)
        .map((e) => ({ altro: otherHolder(s, ordine, e.spedizione), spedizione: e.spedizione }))
        .find((x) => x.altro !== undefined);
    if (shared) {
        // Cancelling the carrier's shipment would cancel the other order's parcels too.
        return refuse(RULE, 'Spedizione unita a un altro ordine', `${ordine} è nella stessa spedizione ${r.v.corriere} di ${shared.altro} (${shared.spedizione}): ` +
            "annullarla annullerebbe anche l'altro ordine. Decidi a mano in WebLabeling.");
    }
    return accept(withCarrier(s, ordine, r.sp, {
        ...r.v,
        fase: 'annullamento',
        annullamentoDa: adesso.toISOString(),
        annullatoDa: { chi, quando: adesso.toISOString() },
        problema: null,
    }), 'Annullamento avviato', `${ordine}: richiesta di annullamento al corriere.`);
}
/**
 * The carrier cancelled: labels void, shipment reopened and to fix. The
 * weights go too, on every parcel: a cancellation often follows a wrong
 * weight («Collo ripesato»), and a weight is never overwritten. Cleared, the
 * next round reads the latest weighing again, or the operator types one.
 */
export function cancelDone(s, ordine) {
    const r = inPhase(s, ordine, ['annullamento'], 'non è in annullamento');
    if ('rifiuto' in r)
        return r.rifiuto;
    const next = {
        ...r.sp,
        colli: r.sp.colli.map(({ pesoManuale: _manuale, ...c }) => ({
            ...c,
            pesoKg: null,
            volumeCm3: null,
        })),
        chiusa: false,
        vettore: {
            ...r.v,
            fase: 'da-sistemare',
            annullamentoDa: null,
            etichette: r.v.etichette.map((e) => ({ ...e, annullata: true })),
            problema: {
                titolo: 'Spedizione annullata',
                motivo: 'Le etichette sono annullate e i pesi azzerati: ripesa i colli, poi ricrea la spedizione.',
                campi: [],
            },
        },
    };
    return accept({ ...s, spedizioni: { ...s.spedizioni, [ordine]: next } }, 'Spedizione annullata', `${ordine}: etichette annullate.`);
}
/** The carrier refused to cancel: the shipment stays created, with the reason. */
export function cancelRefused(s, ordine, problema) {
    const r = inPhase(s, ordine, ['annullamento'], 'non è in annullamento');
    if ('rifiuto' in r)
        return r.rifiuto;
    return accept(withCarrier(s, ordine, r.sp, {
        ...r.v,
        fase: 'creata',
        annullamentoDa: null,
        problema,
    }), problema.titolo, `${ordine}: ${problema.motivo}`);
}
/** A cancellation that has gone on too long goes back to «created» with a problem. */
export function cancelExpired(s, ordine, adesso, limiteMs) {
    const r = inPhase(s, ordine, ['annullamento'], 'non è in annullamento');
    if ('rifiuto' in r)
        return r.rifiuto;
    const since = r.v.annullamentoDa ? new Date(r.v.annullamentoDa).getTime() : adesso.getTime();
    if (adesso.getTime() - since <= limiteMs) {
        return accept(s, 'Annullamento in corso', `${ordine}: si aspetta ancora il corriere.`);
    }
    return accept(withCarrier(s, ordine, r.sp, {
        ...r.v,
        fase: 'creata',
        annullamentoDa: null,
        problema: {
            titolo: CANCEL_NOT_CONFIRMED,
            motivo: `Il corriere non ha confermato l'annullamento: riprova o controlla da ${r.v.corriere}.`,
            campi: [],
        },
    }), 'Annullamento non riuscito', `${ordine}: il corriere non ha confermato.`);
}
/** What the printing station finds when it beeps a parcel barcode. */
export function labelFor(s, barcode) {
    const sp = Object.values(s.spedizioni).find((x) => x.colli.some((c) => c.barcode === barcode));
    if (!sp)
        return { esito: 'sconosciuto', motivo: `Nessun collo ha il codice ${barcode}.` };
    const labels = sp.vettore?.etichette.filter((e) => e.collo === barcode) ?? [];
    const label = labels.find((e) => !e.annullata) ?? labels[0];
    if (label?.annullata) {
        return { esito: 'annullata', motivo: "L'etichetta di questo collo è stata annullata." };
    }
    // BRT may have cancelled after all: printing it could send goods on a void label.
    if (sp.vettore?.problema?.titolo === CANCEL_NOT_CONFIRMED) {
        return {
            esito: 'non-pronta',
            motivo: `Annullamento non confermato da ${sp.vettore.corriere}: verifica prima di stampare.`,
        };
    }
    // A shipment merged by the carrier waits for a person, but its labels are live parcels.
    const usable = sp.vettore?.fase === 'creata' || sp.vettore?.fase === 'da-sistemare';
    if (usable && label && !label.annullata)
        return { esito: 'pronta', file: label.file };
    return { esito: 'non-pronta', motivo: "L'etichetta di questo collo non è ancora pronta." };
}
/** Shipments waiting for someone: to fix, or created/cancelling with a problem. */
export function toFix(s) {
    return Object.values(s.spedizioni).flatMap((sp) => {
        const v = sp.vettore;
        if (!v)
            return [];
        const wanted = v.fase === 'da-sistemare' || (v.problema !== null && v.fase !== 'creazione');
        if (!wanted)
            return [];
        // An order the plan already let go still has a name: the consignee's.
        const cliente = s.piano.find((o) => o.ordine === sp.ordine)?.cliente ?? v.destinatario.nome;
        return [{ ordine: sp.ordine, cliente, vettore: v }];
    });
}
//# sourceMappingURL=carrier.js.map